import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { Skeleton } from "@akuna/ui";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamMember } from "@/hooks/useTeam";
import { cn } from "@/lib/utils";
import { TeamAccessDenied } from "../TeamAccessDenied";
import { MemberHeader } from "./MemberHeader";
import { MemberDataTab } from "./MemberDataTab";
import { MemberPermissionsTab } from "./MemberPermissionsTab";
import { MemberCompensationTab } from "./MemberCompensationTab";
import "./registerCommissionSection";

export const MEMBER_TABS = [
  { key: "data", slug: "dados" },
  { key: "permissions", slug: "permissoes" },
  { key: "compensation", slug: "remuneracao" },
] as const;

export default function TeamMemberPage() {
  const { t } = useTranslation("team");
  const { userId, tab } = useParams<{ userId: string; tab?: string }>();
  const { user } = useAuth();
  const { hasPermission: canView, isLoading: loadingPerm } = useHasPermissionSync("team", "view");
  const { data: member, isLoading } = useTeamMember(userId);

  const active = MEMBER_TABS.find((x) => x.slug === tab);
  if (!active) return <Navigate to={`/team/${userId}/dados`} replace />;
  if (loadingPerm) return null;
  if (!canView && userId !== user?.id) return <TeamAccessDenied />;

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-6 px-4 pb-12 pt-6 md:px-6">
      <div className="flex flex-col gap-4">
        <Link
          to="/team"
          className="inline-flex min-h-8 items-center gap-1.5 self-start rounded-sm text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t("member.back")}
        </Link>
        {isLoading ? (
          <Skeleton className="h-14 w-72" label={t("member.loading")} />
        ) : member ? (
          <MemberHeader member={member} />
        ) : (
          <p role="alert" className="text-sm text-destructive">{t("member.notFound")}</p>
        )}
        <nav aria-label={t("member.tabsLabel")} className="flex flex-wrap gap-6 border-b border-border">
          {MEMBER_TABS.map((item) => (
            <Link
              key={item.key}
              to={`/team/${userId}/${item.slug}`}
              aria-current={item.slug === active.slug ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex min-h-11 items-center border-b-2 px-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                item.slug === active.slug
                  ? "border-primary font-semibold text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`member.tabs.${item.key}`)}
            </Link>
          ))}
        </nav>
      </div>
      {member && active.key === "data" && <MemberDataTab member={member} />}
      {member && active.key === "permissions" && <MemberPermissionsTab member={member} />}
      {member && active.key === "compensation" && <MemberCompensationTab member={member} />}
    </div>
  );
}
